import React, { FC, useState } from "react";
import {
  AckErrorMessage,
  AckLoader,
  AckLogger,
  AckModelForm,
  AckMultiModelForm,
  FormModes,
  useEffectLoader,
  useFetch,
  useLoader,
} from "ackbox-weblib-react";
import { TRANSACTIONS_IMPORT_API_PATH } from "./Constants";

enum ImportType {
  INCOME = "INCOME",
  EXPENSE = "EXPENSE",
  TRANSFER = "TRANSFER",
}

const ImportedBanner = () => {
  return (
    <article className="message is-success is-light">
      <div className="message-body">
        <strong>Import successful</strong><br/>
      </div>
    </article>
  );
};

const ImportParse: FC<{ onComplete: (data: any) => void }> = (props) => {
  const [spec, setSpec] = useState([] as any[]);
  const ackLoader = useLoader();
  const ackFetch = useFetch(TRANSACTIONS_IMPORT_API_PATH);

  const onComponentLoad = async () => {
    const spec = await ackFetch.get("new/");
    setSpec(spec);
  };

  useEffectLoader(ackLoader, onComponentLoad);

  const onSubmit = (data: any) => {
    ackLoader.load(async () => {
      const formData = new FormData();
      formData.append("file", data.file[0]);
      formData.append("prediction", data.prediction === "true" ? "true" : "false");
      const request = { method: "POST", body: formData };
      const imports = await ackFetch.post(`parse/${data.type}/`, request, true);
      props.onComplete(imports);
    });
  };

  return (
    <>
      <h1 className="subtitle">Select File</h1>
      <AckErrorMessage errors={ackLoader.errors}/>
      <AckLoader isLoading={ackLoader.isLoading}/>
      {!ackLoader.isLoading && <AckModelForm spec={spec} mode={FormModes.WRITE} onSubmit={onSubmit}/>}
    </>
  );
};

const ImportSelect: FC<{ importData: any }> = (props) => {
  const [incomeSpec, setIncomeSpec] = useState([] as any[]);
  const [expenseSpec, setExpenseSpec] = useState([] as any[]);
  const [incomes, setIncomes] = useState([] as any[]);
  const [expenses, setExpenses] = useState([] as any[]);
  const ackLoader = useLoader();

  const onComponentLoad = () => {
    setIncomeSpec(props.importData.incomeSpec);
    setExpenseSpec(props.importData.expenseSpec);
    setIncomes(props.importData.incomes);
    setExpenses(props.importData.expenses);
  };

  useEffectLoader(ackLoader, onComponentLoad, [props.importData]);

  return (
    <>
      <h1 className="subtitle">Select Transactions</h1>
      <AckErrorMessage errors={ackLoader.errors}/>
      <AckLoader isLoading={ackLoader.isLoading}/>
      <ImportSelectIncomes spec={incomeSpec} incomes={incomes}/>
      <hr/>
      <ImportSelectExpenses spec={expenseSpec} expenses={expenses}/>
      <hr/>
      <a className="button" href="#/">Back</a>
    </>
  );
};

const ImportSelectIncomes: FC<{ spec: any[], incomes: any[] }> = (props) => {
  const [spec, setSpec] = useState([] as any[]);
  const [incomes, setIncomes] = useState([] as any[]);
  const [imported, setImported] = useState(false);
  const ackLoader = useLoader();
  const ackFetch = useFetch(TRANSACTIONS_IMPORT_API_PATH);

  const onComponentLoad = () => {
    setSpec(props.spec);
    setIncomes(props.incomes);
  };

  const onSubmit = (data: any[]) => {
    ackLoader.load(async () => {
      AckLogger.info("Transactions to be imported from incomes section", JSON.stringify(data));
      const incomeItems = data.filter((item: any) => item.importType === ImportType.INCOME);
      const transferItems = data.filter((item: any) => item.importType === ImportType.TRANSFER);

      if (incomeItems.length > 0) {
        await ackFetch.post("import/incomes/", incomeItems);
      }
      if (transferItems.length > 0) {
        await ackFetch.post("import/transfers/", transferItems);
      }
      setImported(true);
    });
  };

  useEffectLoader(ackLoader, onComponentLoad, [props.spec, props.incomes]);

  return (
    <div className="box mb-5">
      <h2 className="title is-spaced is-size-4">Incomes</h2>
      <AckErrorMessage errors={ackLoader.errors}/>
      {!ackLoader.isLoading && imported && <ImportedBanner/>}
      {!ackLoader.isLoading && !imported && incomes.length === 0 &&
          <h2 className="subtitle is-size-6">Nothing to import</h2>}
      {!ackLoader.isLoading && !imported && incomes.length > 0 && <AckMultiModelForm size={incomes.length}
                                                                                     spec={spec}
                                                                                     values={incomes}
                                                                                     mode={FormModes.WRITE}
                                                                                     onSubmit={onSubmit}/>}
    </div>
  );
};

const ImportSelectExpenses: FC<{ spec: any[], expenses: any[] }> = (props) => {
  const [spec, setSpec] = useState([] as any[]);
  const [expenses, setExpenses] = useState([] as any[]);
  const [imported, setImported] = useState(false);
  const ackLoader = useLoader();
  const ackFetch = useFetch(TRANSACTIONS_IMPORT_API_PATH);

  const onComponentLoad = () => {
    setSpec(props.spec);
    setExpenses(props.expenses);
  };

  const onSubmit = (data: any[]) => {
    ackLoader.load(async () => {
      AckLogger.info("Transactions to be imported from expenses section", JSON.stringify(data));
      const expenseItems = data.filter((item: any) => item.importType === ImportType.EXPENSE);
      const transferItems = data.filter((item: any) => item.importType === ImportType.TRANSFER);

      if (expenseItems.length > 0) {
        await ackFetch.post("import/expenses/", expenseItems);
      }
      if (transferItems.length > 0) {
        await ackFetch.post("import/transfers/", transferItems);
      }
      setImported(true);
    });
  };

  useEffectLoader(ackLoader, onComponentLoad, [props.spec, props.expenses]);

  return (
    <div className="box mb-5">
      <h2 className="title is-spaced is-size-4">Expenses</h2>
      <AckErrorMessage errors={ackLoader.errors}/>
      {!ackLoader.isLoading && imported && <ImportedBanner/>}
      {!ackLoader.isLoading && !imported && expenses.length === 0 &&
          <h2 className="subtitle is-size-6">Nothing to import</h2>}
      {!ackLoader.isLoading && !imported && expenses.length > 0 && <AckMultiModelForm size={expenses.length}
                                                                                      spec={spec}
                                                                                      values={expenses}
                                                                                      mode={FormModes.WRITE}
                                                                                      onSubmit={onSubmit}/>}
    </div>
  );
};

enum ImportStep {
  ImportParse,
  ImportSelect
}

export const TransactionsImport = () => {
  const [step, setStep] = useState(ImportStep.ImportParse);
  const [importData, setImportData] = useState({});

  const onParsingComplete = (imports: any) => {
    setImportData(imports);
    setStep(ImportStep.ImportSelect);
  };

  return (
    <div className="section">
      <h1 className="title is-spaced">Import Transactions</h1>
      {step === ImportStep.ImportParse && <ImportParse onComplete={onParsingComplete}/>}
      {step === ImportStep.ImportSelect && <ImportSelect importData={importData}/>}
    </div>
  );
};
